import { useId, useRef, useState, type FormEvent } from 'react';
import { BanIcon, PenLineIcon, UnlockIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSubmit } from '../../hooks/useSubmit';
import { useRequestId } from '../../hooks/useRequestId';
import { newRequestId } from '../../core/api/http';
import { blockVoucher, correctVoucher } from '../adminApi';
import { Field } from '../ui/Field';
import { Spinner } from '../../ui/Spinner';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { received } from '../inbox/inboxRules';
import { money, parseEuroInput } from '../../core/format/currency';
import { GIFTCARD_MAX_CENTS } from '../../core/rules/kassa';
import { BIG_TOPUP_CENTS } from '../ui/topUpLimits';
import { signedAmount } from './voucherLabels';
import type { Voucher } from '../../core/types/vouchers';
type Props = {
  voucher: Voucher;
  onChanged: (next: Voucher) => void;
};
type Open = 'correct' | 'block' | null;

/**
 * Wat Sarah met een bon kan doen als er iets mis is: het saldo corrigeren (een correctieregel met
 * reden; het grootboek verandert nooit achteraf) of de bon blokkeren (kwijt, gestolen, verkeerd
 * uitgegeven). Een blokkade is op te heffen; het saldo blijft staan en telt als openstaand.
 */
export function VoucherActions({
  voucher: v,
  onChanged
}: Props) {
  const [open, setOpen] = useState<Open>(null);
  const expired = v.status === 'verlopen';
  if (expired) return <p className="text-[13px] text-muted" data-id="element-5557" data-mp="167xne7pva3pk">Verlopen: deze bon is niet meer te corrigeren of te blokkeren.</p>;
  const done = (next: Voucher, message: string) => {
    onChanged(next);
    setOpen(null);
    toast.success(message);
  };
  return <div className="space-y-3" data-id="element-5558" data-mp="x9lqxp2bkbp7">
      {v.blocked && <p className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-5559" data-mp="118chteg6yx3v">
          Geblokkeerd sinds {received(v.blocked.at)}: {v.blocked.reason}
        </p>}
      {open === 'correct' ? <CorrectionForm voucher={v} onClose={() => setOpen(null)} onDone={next => done(next, 'Saldo gecorrigeerd')} data-id="element-5560" data-mp="2vm3w4r9gu55n" /> : open === 'block' ? <BlockForm voucher={v} onClose={() => setOpen(null)} onDone={next => done(next, next.blocked ? 'Bon geblokkeerd' : 'Blokkade opgeheven')} data-id="element-5561" data-mp="8cblghfusy30" /> : <div className="flex flex-wrap gap-2" data-id="element-5562" data-mp="3vk6x631b8edr">
          <button type="button" onClick={() => setOpen('correct')} className={btnSecondary} data-id="element-5563" data-mp="3qkatk4wiyoyq">
            <PenLineIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5564" data-mp="r5617dlawu8a" />
            Saldo corrigeren
          </button>
          <button type="button" onClick={() => setOpen('block')} className={btnSecondary} data-id="element-5565" data-mp="n3c4thi52cdx">
            {v.blocked ? <UnlockIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5566" data-mp="ibu1wnwk21qb" /> : <BanIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5567" data-mp="de0vbtrcb5pi" />}
            {v.blocked ? 'Blokkade opheffen' : 'Blokkeren'}
          </button>
        </div>}
    </div>;
}
type FormProps = {
  voucher: Voucher;
  onClose: () => void;
  onDone: (next: Voucher) => void;
};
function CorrectionForm({
  voucher: v,
  onClose,
  onDone
}: FormProps) {
  const amountId = useId();
  const reasonId = useId();
  const [direction, setDirection] = useState<1 | -1>(-1);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(correctVoucher);
  const raw = parseEuroInput(amount);
  const value = raw === null ? 0 : raw * direction;
  const amountProblem = raw === null || raw <= 0 ? 'Vul een bedrag in, bijv. 15 of 12,50.' : direction === 1 && raw > GIFTCARD_MAX_CENTS ? `Maximaal ${money(GIFTCARD_MAX_CENTS)} erbij.` : v.balanceCents + value < 0 ? `Er staat ${money(v.balanceCents)} op; zoveel kan er niet af.` : null;
  const reasonProblem = reason.trim() ? null : 'Een reden is verplicht; die staat in de geschiedenis.';
  const attempt = `${value}|${reason.trim()}`;
  const requestId = useRequestId(attempt);
  // Veel erbij: eerst in het formulier zelf bevestigen. Een ander bedrag vraagt opnieuw.
  const big = direction === 1 && raw !== null && raw > BIG_TOPUP_CENTS;
  const [confirmedFor, setConfirmedFor] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const needsConfirm = big && confirmedFor !== attempt;
  const showConfirm = needsConfirm && asking === attempt;
  const amountError = fieldErrors.amountCents ?? (tried ? amountProblem : null);
  const reasonError = fieldErrors.reason ?? (tried ? reasonProblem : null);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (amountProblem) return document.getElementById(amountId)?.focus();
    if (reasonProblem) return document.getElementById(reasonId)?.focus();
    if (needsConfirm) {
      if (asking !== attempt) return setAsking(attempt);
      setConfirmedFor(attempt);
    }
    const next = await submit(v.id, {
      requestId,
      amountCents: value,
      reason: reason.trim()
    });
    if (next) onDone(next);
  };
  return <form onSubmit={e => void onSubmit(e)} noValidate className="space-y-4 rounded-md bg-shell p-3.5" data-id="element-5568" data-mp="pldwrc1zkhcs">
      <label className="block" data-id="element-5569" data-mp="unbmmp4w6aep">
        <span className="sr-only" data-id="element-5570" data-mp="gm8ze925uu27">Erbij of eraf</span>
        <select value={direction} onChange={e => setDirection(e.target.value === '1' ? 1 : -1)} className={inputCls} data-id="element-5571" data-mp="gntsdu8tu4xy">
          <option value="-1" data-id="element-5572" data-mp="lng6e5whbp7t">Eraf</option>
          <option value="1" data-id="element-5573" data-mp="19aekwy11gn33">Erbij</option>
        </select>
      </label>
      <Field label="Bedrag (€)" data-id="element-5574" data-mp="19daj8ngstds2">
        <input id={amountId} inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} aria-invalid={amountError ? true : undefined} aria-describedby={amountError ? `${amountId}-error` : undefined} className={`${inputCls} tabular-nums`} data-id="element-5575" data-mp="1dcrn5cgku2v4" />
      </Field>
      {amountError && <p id={`${amountId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-5576" data-mp="1x08un6q29vj4">
          {amountError}
        </p>}
      <Field label="Reden" hint="Bijvoorbeeld: in de salon gebruikt zonder kassa, of fout bij uitgifte." data-id="element-5577" data-mp="26wr73d5g8w9d">
        <input id={reasonId} value={reason} onChange={e => setReason(e.target.value)} maxLength={200} aria-invalid={reasonError ? true : undefined} aria-describedby={reasonError ? `${reasonId}-error` : undefined} className={inputCls} data-id="element-5578" data-mp="21x4pysmi2vva" />
      </Field>
      {reasonError && <p id={`${reasonId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-5579" data-mp="1nzga5z7aiex3">
          {reasonError}
        </p>}
      {error && !fieldErrors.amountCents && !fieldErrors.reason && <p role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-5580" data-mp="1e5pot36gmklm">
          {error}
        </p>}
      {showConfirm && <p role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-5581" data-mp="148kx6ntuhwxf">
          Weet je het zeker? {money(value)} erbij.
        </p>}
      <div className="flex flex-wrap gap-2" data-id="element-5582" data-mp="3bvea8o2fg3aw">
        <button type="submit" disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-5583" data-mp="3bumend8ty5km">
          {submitting && <Spinner data-id="element-5584" data-mp="3fvepgaxcoij0" />}
          {submitting ? 'Bezig…' : showConfirm ? `Ja, ${money(value)} erbij` : raw && !amountProblem ? `${signedAmount('cents', value)} opslaan` : 'Opslaan'}
        </button>
        <button type="button" onClick={onClose} disabled={submitting} className={btnSecondary} data-id="element-5585" data-mp="3punh2x4qtl27">
          Niet corrigeren
        </button>
      </div>
    </form>;
}
function BlockForm({
  voucher: v,
  onClose,
  onDone
}: FormProps) {
  const reasonId = useId();
  const blocking = !v.blocked;
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(blockVoucher);
  const reasonProblem = blocking && !reason.trim() ? 'Waarom blokkeer je hem? Dat zie je later terug.' : null;
  const request = useRef(newRequestId());
  const reasonError = fieldErrors.reason ?? (tried ? reasonProblem : null);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (reasonProblem) return document.getElementById(reasonId)?.focus();
    const next = await submit(v.id, {
      requestId: request.current,
      blocked: blocking,
      reason: reason.trim()
    });
    if (next) onDone(next);
  };
  return <form onSubmit={e => void onSubmit(e)} noValidate className="space-y-4 rounded-md bg-shell p-3.5" data-id="element-5586" data-mp="1bthw3kc22kur">
      <p className="text-[13px] text-ink" data-id="element-5587" data-mp="1qpzayljgehm2">
        {blocking ? 'Een geblokkeerde bon werkt niet meer in de kassa en de webshop. Het saldo blijft staan; opheffen kan altijd.' : 'Na opheffen werkt de bon weer gewoon, met het saldo dat erop staat.'}
      </p>
      <Field label={blocking ? 'Reden' : 'Reden (optioneel)'} hint={blocking ? 'Bijvoorbeeld: kwijt volgens de klant, of per ongeluk twee keer uitgegeven.' : undefined} data-id="element-5588" data-mp="1vjbkrpjh4jur">
        <input id={reasonId} value={reason} onChange={e => setReason(e.target.value)} maxLength={200} aria-invalid={reasonError ? true : undefined} aria-describedby={reasonError ? `${reasonId}-error` : undefined} className={inputCls} data-id="element-5589" data-mp="24j72yebg5f4a" />
      </Field>
      {reasonError && <p id={`${reasonId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-5590" data-mp="2e6azluneqzso">
          {reasonError}
        </p>}
      {error && !fieldErrors.reason && <p role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-5591" data-mp="25dz8s7odtac6">
          {error}
        </p>}
      <div className="flex flex-wrap gap-2" data-id="element-5592" data-mp="2a6imd4n2k5gz">
        <button type="submit" disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-5593" data-mp="1vgbetjylo6v2">
          {submitting && <Spinner data-id="element-5594" data-mp="20empfwwpylec" />}
          {submitting ? 'Bezig…' : blocking ? 'Blokkeren' : 'Blokkade opheffen'}
        </button>
        <button type="button" onClick={onClose} disabled={submitting} className={btnSecondary} data-id="element-5595" data-mp="1mgfzr41xbrpr">
          Annuleren
        </button>
      </div>
    </form>;
}